/**
 * KB-4564 — the stored drop-off/pick-up window on a rebuild of an EXISTING offer: the window's sibling of
 * `storedSessionPrice.ts` (KB-3733), applied at the same two seams (`prepareOfferDataServer`, the web
 * `prepareOfferData`) right after `applyStoredSessionPrice`.
 *
 * THE DEFECT. Both preparers rebuild `variationData` from the program variation, and KB-4516 fills the
 * window from it (`sessionWindowFill`). The stored offer's window was never read, so a hand save, an admin
 * republish, the shared-program sync and the collision re-key all overwrote a window the offer already held
 * — a vendor-stated one (KB-4535, stamped `observedWindow`), one approved from the Inbox (KB-4552), or one
 * set by hand — with the program's.
 *
 * THE RULE, per group (drop-off, pick-up: the four keys of `SESSION_WINDOW_DEFAULTS`), on an existing offer:
 *   - the stored offer holds the group (any key set; null counts as unset, as in `sessionWindowFill`) →
 *     the stored group is carried exactly, key for key;
 *   - except a group whose four keys all equal the stored offer's OLD programming bound (`from`): that is the
 *     writer's own default (KB-3613), not anybody's window, so it follows the program — a re-time moves it,
 *     as `author-campLocation-variation` does. A group held at its `observedWindow` stamp never takes this
 *     exception: a vendor that states drop-off at the programming start has stated it;
 *   - a group the stored offer does not hold takes the rebuild's (the program's window, or the default).
 * A MOVE to another program (the collision re-key, the session dialog's Program selector: the variation being
 * written is not the stored `variationKey`) carries only a group held at its `observedWindow` stamp — what the
 * vendor stated is the session's, wherever it moves (as `moveOfferVariation` keeps it); any other stored window
 * was set against the old program and takes the new one's.
 * A rebuild whose program has no `timings` at all likewise carries only a stamp-held group (the nightly's
 * window-only timings), and otherwise writes none. A create (`storedOffer` null) and a placeholder (its timings
 * are the program's, as curated — KB-4516's gate) are untouched. Nothing here reads or writes `observedWindow`
 * beyond comparing against it.
 *
 * Pure: no Firestore, no clock — imported by the functions seams AND the web client (`@/functions-shared`).
 */
import { SESSION_WINDOW_DEFAULTS, observedWindowTimings, sessionWindowFill } from "./sessionWindowRule";

type Timings = Record<string, unknown>;
type WindowGroup = { from: string; keys: ReadonlyArray<string> };

const GROUPS: ReadonlyArray<WindowGroup> = Object.values(SESSION_WINDOW_DEFAULTS as Record<string, WindowGroup>);
const WINDOW_KEYS: ReadonlyArray<string> = GROUPS.flatMap((g) => g.keys);

const has = (o: Timings, k: string) => Object.prototype.hasOwnProperty.call(o, k);
const isSet = (v: unknown) => v !== undefined && v !== null;
const asTimings = (v: unknown): Timings => (v && typeof v === "object" ? (v as Timings) : {});

/** The stored offer's `variationData.timings`, or `{}`. */
function storedTimingsOf(storedOffer: Record<string, unknown>): Timings {
  return asTimings(asTimings(storedOffer.variationData).timings);
}

/**
 * The timings a rebuild should write, given the rebuilt (program) timings and the stored offer. `variationKey` is
 * the program being written; it differing from the stored offer's is a move. Returns `rebuilt` itself (same
 * object) when every group follows the rebuild. A program with no `timings` carries only stamp-held groups.
 */
export function carryStoredWindow(
  rebuilt: Timings | undefined,
  storedOffer: Record<string, unknown>,
  variationKey?: unknown,
): Timings | undefined {
  // A program with no `timings` gets none invented, EXCEPT a group held at its `observedWindow` stamp: the
  // nightly writes window-only timings for a vendor-stated window (offerComputeCore), and that is the session's.
  const programless = !rebuilt || typeof rebuilt !== "object";
  const stored = storedTimingsOf(storedOffer);
  const stamp = observedWindowTimings(storedOffer.observedWindow) as Timings;
  // A stored offer with no recorded variationKey is not treated as a move: there is nothing to say it moved.
  const moved = typeof storedOffer.variationKey === "string" && variationKey !== undefined && variationKey !== storedOffer.variationKey;
  const carried = GROUPS.filter(({ from, keys }) => {
    if (!keys.some((k) => isSet(stored[k]))) return false;
    const heldAtStamp = keys.every((k) => has(stamp, k) && stored[k] === stamp[k]);
    if (moved || programless) return heldAtStamp;
    const isOldBoundDefault = Number.isFinite(stored[from]) && keys.every((k) => stored[k] === stored[from]);
    return heldAtStamp || !isOldBoundDefault;
  });
  if (programless && !carried.length) return rebuilt;
  const base: Timings = programless ? {} : (rebuilt as Timings);
  const differs = carried.some(({ keys }) => keys.some((k) => has(stored, k) !== has(base, k) || stored[k] !== base[k]));
  if (!differs) return rebuilt;
  const t: Timings = { ...base };
  for (const { keys } of carried) {
    for (const k of keys) {
      if (has(stored, k)) t[k] = stored[k];
      else delete t[k];
    }
  }
  return t;
}

/**
 * The single window decision every rebuild seam makes, on the core's output: an existing, non-placeholder
 * offer carries its stored window (`carryStoredWindow`); a create or a placeholder returns `core` untouched.
 */
export function applyStoredSessionWindow<T extends { sanitizedVariationData: any }>(
  core: T,
  storedOffer: Record<string, unknown> | null,
  opts: { isPlaceholder: boolean; variationKey: unknown },
): T {
  if (!storedOffer || opts.isPlaceholder) return core;
  const vd = core.sanitizedVariationData;
  const timings = vd && typeof vd === "object" ? vd.timings : undefined;
  const next = carryStoredWindow(timings, storedOffer, opts.variationKey);
  if (next === timings) return core;
  return { ...core, sanitizedVariationData: { ...(vd || {}), timings: next } };
}

/**
 * The stored doc with the window an EDIT explicitly sets laid over it — what `updateOffer` treats as the
 * offer's own window. An edit carries `variationData` only when it means to set it: `EntityHistoryPanel`
 * restores a timings leaf by sending the whole `variationData` back (`buildRestorePatch`), and an Inbox
 * accept of a `variationData` proposal sends the proposed value; the session dialog never sends it. Without
 * this, the carry would put the PRE-edit window back over a restore. Only the window keys are taken from the
 * edit; an edit with no `variationData.timings` leaves the stored doc as it is (same object).
 */
export function withSessionWindowEdit(stored: Record<string, unknown>, edit: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const editVd = edit && has(edit, "variationData") ? edit.variationData : undefined;
  if (!editVd || typeof editVd !== "object" || !has(editVd as Timings, "timings")) return stored;
  const editTimings = asTimings((editVd as Timings).timings);
  const t: Timings = { ...storedTimingsOf(stored) };
  for (const k of WINDOW_KEYS) {
    if (has(editTimings, k)) t[k] = editTimings[k];
    else delete t[k];
  }
  return { ...stored, variationData: { ...asTimings(stored.variationData), timings: t } };
}

/**
 * Ruling B (card aacaa3bc, directive e0e0b91c) — the stored doc with every window group that merely COPIED the
 * offer's OLD program released (its four keys nulled), so the carry above hands that group the NEW program's
 * window and a re-time reaches it. "Copied" is: all four keys equal what the old program produced — its explicit
 * window, or its KB-3613 default from its old bound (`sessionWindowFill`). A group held at its `observedWindow`
 * stamp is never released (a stated window that happens to equal the old program's stays the vendor's), nor is
 * any other group (approved, hand-set). `priorProgramTimings` undefined means the old program is unknown: the
 * stored doc is returned as it is (same object), and every held group is carried. Applied by a caller that knows
 * the old program (`updateAllOffersForLocation`, given the pre-save variations); the server seams do not.
 */
export function releaseOldProgramWindow(stored: Record<string, unknown>, priorProgramTimings: unknown): Record<string, unknown> {
  if (priorProgramTimings === undefined) return stored;
  const prior = asTimings(priorProgramTimings);
  const oldProduced: Timings = { ...prior, ...sessionWindowFill(prior) };
  const t = storedTimingsOf(stored);
  const stamp = observedWindowTimings(stored.observedWindow) as Timings;
  const released = GROUPS.filter(({ keys }) => {
    // Equal key for key (a partly-set program window — a start with no end — is still the program's), and the
    // old program held the group at all (an unset old group produced nothing to copy).
    const copiesOld = keys.some((k) => isSet(oldProduced[k])) && keys.every((k) => t[k] === oldProduced[k]);
    const heldAtStamp = keys.every((k) => has(stamp, k) && t[k] === stamp[k]);
    return copiesOld && !heldAtStamp;
  });
  if (!released.length) return stored;
  const next: Timings = { ...t };
  for (const { keys } of released) for (const k of keys) next[k] = null;
  return { ...stored, variationData: { ...asTimings(stored.variationData), timings: next } };
}
